import React from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { parse } from 'query-string';
import { useLocation } from 'react-router-dom';
import { CaretDownMaterialIcon, DateDropdown, palette, SecondaryButton } from '@dev/ee-components';

import dayjs from '@/services/tools/dayjs';
import { ContactGetGrowthStart, ContactsOverviewStartLoading } from '@/store/contacts/actions';
import { useDateFilterData } from '@/services/tools/date';
import { useDateDropdownLabel } from '@/services/hooks/useDateDropdownLabel';
import { DateRangeInterface } from '../ContactOverview';

const CONTACTS_GROWTH_DATE_ZERO = '2020-10-13T00:00:00';

const calculateFromDate = (date: number): string =>
    dayjs(CONTACTS_GROWTH_DATE_ZERO).diff(dayjs().subtract(date, 'day')) < 0
        ? dayjs().subtract(date, 'day').utc().startOf('day').format('YYYY-MM-DDTHH:mm:ss')
        : CONTACTS_GROWTH_DATE_ZERO;

export const DateRangeSelect = ({setContactsGrowthFrom, setContactsGrowthTo, contactsGrowthFrom, contactsGrowthTo, isUrlUpdateDisabled}: DateRangeInterface): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const buttonLabel = useDateDropdownLabel(undefined, contactsGrowthFrom, contactsGrowthTo);
    const location = useLocation();
    const query = parse(location.search);
    
    const startDate = contactsGrowthFrom ?? query?.from?.toString();
    const endDate = contactsGrowthTo ?? query?.to?.toString();

    const dateRangeOptions: { label: string; value: string | number }[] = [...useDateFilterData().slice(0, -1)];

    const handleCalendarChange = (start, end): void => {
        const dateFormat = 'YYYY-MM-DDTHH:mm:ss';
        setContactsGrowthFrom?.(start.startOf('day').add(1, 'day').utc().format(dateFormat));
        setContactsGrowthTo?.(end.endOf('day').utc().format(dateFormat));

        dispatch(
            ContactGetGrowthStart(
                start.startOf('day').add(1, 'day').utc().format(dateFormat),
                end.endOf('day').utc().format(dateFormat),
                isUrlUpdateDisabled
            )
        );
        dispatch(
            ContactsOverviewStartLoading({
                From: start.startOf('day').add(1, 'day').utc().format(dateFormat),
                To: end.endOf('day').utc().format(dateFormat),
            })
        );
    };

    const handleDateDropdownChange = (dropdownItem): void => {
        const To = dayjs().utc().endOf('day').format('YYYY-MM-DDTHH:mm:ss');
        const From = calculateFromDate(dropdownItem.value);
        setContactsGrowthFrom?.(From);
        setContactsGrowthTo?.(To);

        dispatch(ContactGetGrowthStart(From, To, isUrlUpdateDisabled));
        dispatch(ContactsOverviewStartLoading({ From, To }));
    };

    return (
        <DateDropdown
            type={'custom'}
            onItemClick={(e, obj: { label: string; value: string }): void => {
                handleDateDropdownChange(obj);
            }}
            minDate={dayjs(CONTACTS_GROWTH_DATE_ZERO).toJSON() as any}
            minWidth={112}
            maxDate={dayjs().toJSON() as any}
            options={dateRangeOptions}
            calendarOnChange={(start, end): void => {
                handleCalendarChange(start, end);
            }}
            placement={'bottom-end'}
            startDate={startDate}
            endDate={endDate}
            maxHeight={36}
        >
            <SecondaryButton
                text={t(buttonLabel)}
                size={'small'}
                endIcon={<CaretDownMaterialIcon color={palette.indigo[6]} />}
            />
        </DateDropdown>
    );
};
